import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { markMissionComplete, MISSIONS } from '../../lib/tutorialActions';
import {
  Mail, FileText, Download, Loader2, Send, FilePlus,
  CheckCircle2, MessageSquare, XCircle, Clock,
} from 'lucide-react';
import {
  generateOutreach, listOutreachDrafts, sendOutreachDraft,
  generateJedzDraft, getJedzFileUrl,
} from '../../lib/api';


function OutreachSection({ tenderId }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const r = await listOutreachDrafts(tenderId);
      setDrafts(r.items || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchDrafts(); }, [tenderId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      toast.info('Claude generuje pytania wyjaśniające (art. 135 PZP)...');
      const r = await generateOutreach(tenderId);
      toast.success(`Wygenerowano draft (${r.questions_count} pytań)`);
      await fetchDrafts();
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    }
    setGenerating(false);
  };

  const handleSend = async (draftId) => {
    setSending(draftId);
    try {
      const r = await sendOutreachDraft(draftId);
      if (r.status === 'sent') {
        toast.success('Email wysłany do zamawiającego');
      } else if (r.status === 'mock') {
        toast.info('Email (tryb mock — SMTP nie skonfigurowany)');
      } else {
        toast.warning('Status: ' + r.status);
      }
      await fetchDrafts();
    } catch (e) {
      toast.error('Błąd wysyłki: ' + (e.response?.data?.detail || e.message));
    }
    setSending(null);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Mail className="w-4 h-4" /> Proaktywny Outreach
          </CardTitle>
          <Button onClick={handleGenerate} disabled={generating} size="sm">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FilePlus className="w-4 h-4 mr-2" />}
            Generuj draft
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Claude generuje pytania wyjaśniające do zamawiającego zgodnie z art. 135 PZP.
          Draft wymaga zatwierdzenia przed wysyłką.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Wczytuję...
          </div>
        ) : drafts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">Brak draftów</p>
        ) : (
          <div className="space-y-2">
            {drafts.map(d => (
              <div key={d.id || d._id} className="border rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{d.subject}</div>
                    <div className="text-xs text-muted-foreground">
                      Do: {d.to_email || 'brak email'} ·
                      {d.questions_count} pytań ·
                      {new Date(d.created_at).toLocaleDateString('pl-PL')}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      d.status === 'sent' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                      d.status === 'failed' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                      'bg-amber-500/10 text-amber-400 border-amber-500/30'
                    }
                  >
                    {d.status === 'sent' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {d.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                    {d.status === 'draft' && <Clock className="w-3 h-3 mr-1" />}
                    {d.status}
                  </Badge>
                </div>

                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Podgląd treści
                  </summary>
                  <pre className="whitespace-pre-wrap font-mono text-xs mt-2 p-2 bg-muted rounded">
                    {d.body}
                  </pre>
                </details>

                {d.status === 'draft' && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSend(d.id || d._id)}
                      disabled={!d.to_email || sending === (d.id || d._id)}
                    >
                      {sending === (d.id || d._id) ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                      Wyślij
                    </Button>
                    {!d.to_email && (
                      <span className="text-xs text-amber-400">
                        Brak email zamawiającego w analizie — wygeneruj NLP najpierw
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function JedzSection({ tenderId }) {
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await generateJedzDraft(tenderId);
      setData(r);
      toast.success('JEDZ draft wygenerowany');
      markMissionComplete(MISSIONS.GENERATE_JEDZ);
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    }
    setGenerating(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" /> JEDZ / ESPD Generator
          </CardTitle>
          <Button onClick={handleGenerate} disabled={generating} size="sm">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FilePlus className="w-4 h-4 mr-2" />}
            Generuj pakiet
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Generuje szkielet JEDZ (ESPD XML), oświadczenia wykonawcy, oraz checklist dokumentów
          wymaganych do oferty. XML można zaimportować do <a className="text-primary hover:underline"
          href="https://espd.ec.europa.eu/" target="_blank" rel="noopener noreferrer">espd.ec.europa.eu</a>.
        </p>

        {data && (
          <div className="space-y-2 pt-2 border-t">
            <div className="text-xs text-muted-foreground">
              Wygenerowano: {new Date(data.generated_at).toLocaleString('pl-PL')} ·
              {data.checklist_items} pozycji na liście
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {['jedz_draft.xml', 'oswiadczenia.txt', 'checklist.md'].map(fn => (
                <Button
                  key={fn}
                  variant="outline"
                  size="sm"
                  asChild
                >
                  <a
                    href={getJedzFileUrl(tenderId, fn) + `?token=${localStorage.getItem('token') || ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" />
                    {fn}
                  </a>
                </Button>
              ))}
            </div>

            {data.checklist_preview && (
              <details className="text-xs pt-2">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Podgląd checklist
                </summary>
                <pre className="whitespace-pre-wrap font-mono mt-2 p-2 bg-muted rounded max-h-60 overflow-y-auto">
                  {data.checklist_preview}
                </pre>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function ActionsPanel({ tenderId }) {
  return (
    <div className="space-y-4">
      <OutreachSection tenderId={tenderId} />
      <JedzSection tenderId={tenderId} />
    </div>
  );
}
